import React, { useEffect, useRef, useState } from "react";
import { X, ChevronDown, ChevronRight, Check, ListChecks } from "lucide-react";
import { gbp } from "../lib/format";

const cls = (...a) => a.filter(Boolean).join(" ");

/* Library picker used by the Estimate ("From library") AND the Job Template
 * editor ("Add library line"), so both flows look and behave identically:
 * search, This phase / All library toggle (phase-tag smart default), grouped
 * All-library view, RECIPE / THIS PHASE pills, £ per unit ("est." for
 * estimated-each-use items), SKU back-fill on open.
 *
 * Props: open, onClose, onPick(item), items, currentPhaseTags, onBackfill,
 * onPickMany(items) — optional; when given, a "Select multiple" mode (LT-38)
 * shows tick boxes and an "Add N items" footer. */
export function LibraryPickerModal({ open, onClose, onPick, onPickMany, items, currentPhaseTags, onBackfill }) {
  const [q, setQ] = useState("");
  // LT-38 — bulk mode: tick rows, add them all at once.
  const [multi, setMulti] = useState(false);
  const [picked, setPicked] = useState({});   // id → item
  const pickedCount = Object.keys(picked).length;
  const togglePicked = (it) => setPicked((prev) => {
    const next = { ...prev };
    if (next[it.id]) delete next[it.id]; else next[it.id] = it;
    return next;
  });
  // Section 5 — segmented toggle: "this_phase" | "all_library".
  // Smart default: "This phase" when the current phase has matching items,
  // otherwise auto-fall back to "All library" so the user never lands on an
  // empty modal. Computed once per modal-open via the `initRef` guard so the
  // user's manual choice isn't overridden by background data refreshes.
  const [mode, setMode] = useState("this_phase");
  // Group collapse state — only used in All-library view. Phase-tag headers
  // start collapsed (per spec); user-toggled state persists for this modal
  // session and resets on next open.
  const [expanded, setExpanded] = useState({});
  // Section 5 — opening the picker triggers a backend hygiene pass that
  // back-fills missing SKUs and refreshes has_recipe across all items.
  const [backfilling, setBackfilling] = useState(false);

  const initRef = useRef(false);
  // Trigger backfill on each modal-open and reset state on close.
  useEffect(() => {
    if (!open) {
      initRef.current = false;
      setQ("");
      setExpanded({});
      setMulti(false);
      setPicked({});
      return;
    }
    if (initRef.current) return;
    initRef.current = true;
    setBackfilling(true);
    Promise.resolve(onBackfill?.()).finally(() => setBackfilling(false));
  }, [open, onBackfill]);

  // Compute the smart default mode the first time both `open` and `items`
  // are present in this modal session. Subsequent prop updates (e.g. after
  // backfill) don't override the user's manual choice.
  const modeDefaultedRef = useRef(false);
  useEffect(() => {
    if (!open) { modeDefaultedRef.current = false; return; }
    if (modeDefaultedRef.current) return;
    if (!items || !items.length) return;
    modeDefaultedRef.current = true;
    if (!currentPhaseTags || !currentPhaseTags.length) {
      setMode("all_library");
      return;
    }
    const matchCount = items.filter(
      (i) => (i.phase_tags || []).some((t) => currentPhaseTags.includes(t))
    ).length;
    setMode(matchCount > 0 ? "this_phase" : "all_library");
  }, [open, items, currentPhaseTags]);

  if (!open) return null;

  const hasPhaseTags = !!(currentPhaseTags && currentPhaseTags.length);
  const matchesPhase = (it) => (it.phase_tags || []).some((t) => currentPhaseTags.includes(t));
  const matchesQuery = (it) => !q
    || (it.description || "").toLowerCase().includes(q.toLowerCase())
    || (it.sku || "").toLowerCase().includes(q.toLowerCase());

  const baseFiltered = (mode === "this_phase" && hasPhaseTags
    ? items.filter(matchesPhase)
    : items
  ).filter(matchesQuery);

  // Group by phase tag (All-library view only). Items with multiple tags
  // appear under each — keeps the picker honest about cross-phase reusability.
  const groups = (() => {
    if (mode === "this_phase") return null;
    const map = {};
    for (const it of baseFiltered) {
      const tags = (it.phase_tags && it.phase_tags.length) ? it.phase_tags : ["__uncat__"];
      for (const t of tags) {
        (map[t] ||= []).push(it);
      }
    }
    for (const k in map) {
      map[k].sort((a, b) => (a.description || "").localeCompare(b.description || ""));
    }
    const keys = Object.keys(map).filter((k) => k !== "__uncat__")
      .sort((a, b) => a.localeCompare(b));
    if (map["__uncat__"]) keys.push("__uncat__");
    return keys.map((k) => ({
      tag: k === "__uncat__" ? "Uncategorised" : k,
      key: k,
      items: map[k],
      isUncat: k === "__uncat__",
    }));
  })();

  // When user types a search query, auto-expand all groups so results aren't
  // hidden behind a collapsed header.
  const isExpanded = (key) => !!q || !!expanded[key];

  const toggleGroup = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // Single-line item renderer used in both flat and grouped views.
  const renderItem = (it) => {
    const isInThisPhase = hasPhaseTags && matchesPhase(it);
    const isPicked = multi && !!picked[it.id];
    return (
      <button key={it.id} onClick={() => (multi ? togglePicked(it) : onPick(it))}
        data-testid={`library-item-${it.id}`}
        aria-pressed={multi ? isPicked : undefined}
        className={cls("w-full text-left px-5 py-3 hover:bg-surface-2/60 transition-colors group",
          isPicked && "bg-accent-soft/60")}>
        <div className="flex items-start justify-between gap-4">
          {multi && (
            <span
              className={cls("mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border",
                isPicked ? "bg-accent border-accent text-white" : "border-line bg-surface")}
              data-testid={`library-item-${it.id}-check`}
            >
              {isPicked && <Check size={11} />}
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {it.has_recipe && (
                <span className="inline-flex items-center justify-center rounded font-mono"
                  data-testid="library-item-recipe-pill"
                  style={{ background: "#E8EFE9", color: "#2F4F3E", border: "1px solid #D6E2D8",
                           padding: "1px 6px", fontSize: 9, fontWeight: 600, letterSpacing: "0.05em" }}>
                  RECIPE
                </span>
              )}
              {it.flat_material_enabled && (
                <span className="inline-flex items-center justify-center rounded font-mono"
                  data-testid="library-item-flat-pill"
                  title="Materials priced as one flat £/unit figure — nothing itemised to order yet"
                  style={{ background: "#F8F1DF", color: "#7A5A06", border: "1px solid #EAD9A6",
                           padding: "1px 6px", fontSize: 9, fontWeight: 600, letterSpacing: "0.05em" }}>
                  FLAT MATERIAL
                </span>
              )}
              {/* Section 5 — "this phase" badge in All-library view so users
                  can spot items that would have been default-filtered. */}
              {mode === "all_library" && isInThisPhase && (
                <span className="inline-flex items-center justify-center rounded font-mono"
                  data-testid="library-item-this-phase-badge"
                  style={{ background: "#2F4F3E", color: "#FFFFFF",
                           padding: "1px 6px", fontSize: 9, fontWeight: 600, letterSpacing: "0.05em" }}>
                  ✓ THIS PHASE
                </span>
              )}
              {it.sku && (
                <span className="font-mono tnum text-ink-3" style={{ fontSize: 11 }}>{it.sku}</span>
              )}
            </div>
            <div className="text-ink mt-0.5" style={{ fontSize: 12.5, fontWeight: 500 }}>
              {it.description}
            </div>
            {it.phase_tags && it.phase_tags.length > 0 && (
              <div className="text-ink-3 mt-1" style={{ fontSize: 11 }}>
                {it.phase_tags.slice(0, 3).join(" · ")}
                {it.phase_tags.length > 3 ? ` +${it.phase_tags.length - 3}` : ""}
              </div>
            )}
          </div>
          <div className="text-right">
            {/* LT-Estimate-UX-01 Section 4 — mode-2 items don't have a
                real rate. Show "est." (amber) in place of the £ figure so
                the estimator knows the price will land per tender. */}
            {it.labour_mode === "estimated_each_use" ? (
              <span className="font-mono tnum text-warning" style={{ fontSize: 13, fontWeight: 600 }}
                title="Estimated each use — hours entered per tender">
                est.
              </span>
            ) : (
              <span className="font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
                {gbp(it.current_rate || 0)}
              </span>
            )}
            <div className="text-ink-3 font-mono mt-0.5" style={{ fontSize: 10.5 }}>
              per {it.unit}
            </div>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-start justify-center pt-16 px-4">
      <div className="w-full max-w-[720px] bg-surface border border-line rounded-lg shadow-card overflow-hidden"
        data-testid="library-picker-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-ink" style={{ fontSize: 16, fontWeight: 600 }}>Add line from library</h2>
            {backfilling && (
              <span className="text-ink-3" style={{ fontSize: 11 }} data-testid="library-backfill-status">
                Refreshing…
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {onPickMany && (
              <button
                type="button"
                onClick={() => { setMulti((m) => !m); setPicked({}); }}
                data-testid="library-picker-multi-toggle"
                aria-pressed={multi}
                className={cls("inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border transition-colors",
                  multi ? "bg-accent text-white border-accent" : "border-line text-ink-2 hover:bg-surface-2")}
                style={{ fontSize: 11.5, fontWeight: 500 }}
              >
                <ListChecks size={12} /> {multi ? "Selecting multiple" : "Select multiple"}
              </button>
            )}
            <button onClick={onClose} className="text-ink-3 hover:text-ink" data-testid="library-picker-close"><X size={16} /></button>
          </div>
        </div>
        <div className="px-5 py-3 border-b border-line space-y-3">
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="library-picker-search"
            placeholder="Search library by description or SKU…" autoFocus spellCheck="true"
            className="w-full h-9 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            style={{ fontSize: 13 }} />
          {hasPhaseTags && (
            <div className="flex items-center gap-3" data-testid="library-picker-mode-toggle">
              <div className="inline-flex p-0.5 bg-surface-2 rounded-md border border-line">
                <button
                  onClick={() => setMode("this_phase")}
                  data-testid="library-picker-mode-this-phase"
                  className={cls(
                    "px-3 h-7 rounded transition-colors",
                    mode === "this_phase"
                      ? "bg-surface text-accent shadow-sm"
                      : "text-ink-3 hover:text-ink-2"
                  )}
                  style={{ fontSize: 11.5, fontWeight: mode === "this_phase" ? 600 : 500 }}
                >
                  This phase
                </button>
                <button
                  onClick={() => setMode("all_library")}
                  data-testid="library-picker-mode-all-library"
                  className={cls(
                    "px-3 h-7 rounded transition-colors",
                    mode === "all_library"
                      ? "bg-surface text-accent shadow-sm"
                      : "text-ink-3 hover:text-ink-2"
                  )}
                  style={{ fontSize: 11.5, fontWeight: mode === "all_library" ? 600 : 500 }}
                >
                  All library
                </button>
              </div>
              <span className="text-ink-3" style={{ fontSize: 11 }}>
                {mode === "this_phase"
                  ? <>tagged with <span className="font-mono uppercase" style={{ letterSpacing: "0.04em" }}>{currentPhaseTags.join(", ")}</span></>
                  : `${baseFiltered.length} item${baseFiltered.length === 1 ? "" : "s"}`}
              </span>
            </div>
          )}
        </div>
        <div className="max-h-[55vh] overflow-auto">
          {baseFiltered.length === 0 ? (
            <div className="text-center py-12 text-ink-3" style={{ fontSize: 12.5 }} data-testid="library-picker-empty">
              {q
                ? "No matching library items."
                : mode === "this_phase"
                  ? "No items tagged with this phase yet — switch to All library to browse everything."
                  : "Library is empty."}
            </div>
          ) : mode === "this_phase" ? (
            // Flat list — single-group view, no headers (per spec).
            <div className="divide-y divide-line-soft">
              {baseFiltered
                .slice()
                .sort((a, b) => (a.description || "").localeCompare(b.description || ""))
                .map(renderItem)}
            </div>
          ) : (
            // All-library: grouped, collapsible per phase tag.
            <div>
              {groups.map((g) => (
                <div key={g.key} className="border-b border-line-soft last:border-b-0">
                  <button
                    onClick={() => toggleGroup(g.key)}
                    data-testid={`library-picker-group-${g.key}`}
                    className="w-full flex items-center justify-between px-5 py-2.5 hover:bg-surface-2/40 transition-colors"
                    style={{ background: "#F5F3EE" }}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded(g.key)
                        ? <ChevronDown size={12} className="text-ink-3" />
                        : <ChevronRight size={12} className="text-ink-3" />}
                      <span
                        className={cls("font-mono uppercase",
                          g.isUncat ? "text-ink-3" : "text-ink-2")}
                        style={{ fontSize: 10.5, letterSpacing: "0.06em", fontWeight: 700 }}
                      >
                        {g.tag}
                      </span>
                      <span className="text-ink-3" style={{ fontSize: 11 }}>
                        ({g.items.length})
                      </span>
                    </div>
                  </button>
                  {isExpanded(g.key) && (
                    <div className="divide-y divide-line-soft">
                      {g.items.map(renderItem)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {multi && (
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-line bg-surface-2/60" data-testid="library-picker-multi-footer">
            <span className="text-ink-2" style={{ fontSize: 12 }} data-testid="library-picker-multi-count">
              {pickedCount === 0 ? "Tick the items to add — they land at qty 0 for you to fill in." : `${pickedCount} item${pickedCount === 1 ? "" : "s"} selected`}
            </span>
            <div className="flex items-center gap-2">
              {pickedCount > 0 && (
                <button type="button" onClick={() => setPicked({})} className="text-ink-3 hover:text-ink" style={{ fontSize: 12 }} data-testid="library-picker-multi-clear">Clear</button>
              )}
              <button
                type="button"
                disabled={pickedCount === 0}
                onClick={() => onPickMany(Object.values(picked))}
                data-testid="library-picker-multi-add"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-accent text-white hover:bg-accent-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                style={{ fontSize: 12.5, fontWeight: 600 }}
              >
                Add {pickedCount || ""} item{pickedCount === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
